"use client";

import React, { useEffect, useRef, useState } from "react";
import { UserPlus, Link as LinkIcon, TrendingUp, ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const steps = [{
  number: "01",
  title: "Register",
  description: "Submit your initial registration so we can begin the onboarding process.",
  icon: UserPlus
}, {
  number: "02",
  title: "Direct Contact",
  description: "Once registered we contact you directly and guide you through the required account setup.",
  icon: Phone
}, {
  number: "03",
  title: "Prosper",
  description: "Connect your account, allocate funds, and start copying trades automatically.",
  icon: TrendingUp
}];
export default function OnboardingWorkflow() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="onboarding-workflow" className="dark bg-background text-foreground py-24 lg:py-32 overflow-hidden relative">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_center,var(--color-secondary)_0%,transparent_70%)] opacity-5 pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6 text-foreground">Client&nbsp;<span className="text-primary" data-ast-id="f:/src/pages/Home/sections/OnboardingWorkflow.tsx|sig:p8yc2l" data-ast-start="2590" data-ast-end="2638">Onboarding</span></h2>
          <p className="text-lg text-muted-foreground">Start your journey to copytrading in three simple steps.</p>
        </div>

        <div ref={ref} className={`relative transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="absolute top-[40px] left-[16%] right-[16%] h-[2px] border-t-2 border-dashed border-primary/30 hidden md:block z-0 animate-pulse"></div>
          
          {}
          <div className="absolute top-[10%] bottom-[10%] left-1/2 w-[2px] border-l-2 border-dashed border-primary/30 md:hidden z-0 -translate-x-1/2 animate-pulse"></div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-20 md:gap-8">
            {steps.map((step, i) => <div key={step.number} data-index={i} className="relative flex flex-col items-center text-center group">
                {}
                <span className="absolute -top-16 md:-top-20 left-1/2 -translate-x-1/2 text-[8rem] md:text-[10rem] font-black text-secondary/5 select-none pointer-events-none transition-colors duration-500 group-hover:text-secondary/10">
                  {step.number}
                </span>

                {}
                <div className="relative z-10 size-20 rounded-2xl bg-card border border-border flex items-center justify-center mb-8 transition-transform duration-500 group-hover:-translate-y-2">
                  {}
                  <div className="absolute inset-0 rounded-2xl bg-primary/20 blur-xl group-hover:bg-primary/40 transition-colors duration-500 -z-10"></div>
                  <step.icon className="size-10 text-primary relative z-10" />
                </div>

                {}
                <h3 className="text-2xl font-semibold text-foreground mb-4 relative z-10">
                  {step.title}
                </h3>
                <p className="text-muted-foreground max-w-[260px] relative z-10 leading-relaxed">
                  {step.description}
                </p>
              </div>)}
          </div>
        </div>

        {}
        <div className={`mt-24 flex justify-center transition-all duration-1000 delay-300 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Button asChild size="lg" className="rounded-full px-8 h-14 text-lg shadow-[0_0_20px_var(--color-primary)] hover:shadow-[0_0_30px_var(--color-primary)] transition-shadow duration-300">
            <Link to="/AccessPage">
              Start Onboarding <ArrowRight className="ml-2 size-5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}